import type { FC, ReactElement, ReactNode } from "react"
import { cloneElement, isValidElement, memo } from "react"
import { IconCheckDoubleOutline18 as CheckCheckIcon } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

interface Props {
	text?: string
	title?: string
	className?: string
	image?: ReactNode
	action?: ReactNode
	variant?: "center" | "left"
	/** One muted line under the image, the way Linear's filtered lists read. */
	quiet?: boolean
}

export const Empty: FC<Props> = memo(
	({
		className,
		text,
		title = "All empty",
		image,
		action,
		variant = "center",
		quiet = false,
	}) => {
		const isLeftVariant = variant === "left"
		const body = text ?? (quiet ? undefined : "There's nothing here yet")

		const imageElement = image
			? isValidElement(image)
				? cloneElement(image as ReactElement<{ className?: string }>, {
						className: cn(
							"h-auto w-[4.05rem] object-contain select-none opacity-80 dark:opacity-50",
							isLeftVariant && "-ml-1",
							(image as ReactElement<{ className?: string }>).props?.className,
						),
					})
				: image
			: null

		return (
			<div
				className={cn(
					"flex flex-col gap-2 p-4",
					isLeftVariant
						? "items-start justify-start text-left"
						: "items-center justify-center text-center",
					className,
				)}
			>
				{imageElement ?? (
					<CheckCheckIcon className="h-[1.8rem] w-[1.8rem] text-green-500" />
				)}
				<h1
					className={cn(
						"mt-2 select-none",
						quiet
							? "mb-2 max-w-lg text-balance text-[13px] font-normal text-secondary"
							: "text-[14px] text-lg font-semibold",
					)}
				>
					{title}
				</h1>
				{body !== undefined && (
					<p className="mb-3 max-w-lg select-none text-balance text-sm text-secondary">
						{body}
					</p>
				)}
				{action}
			</div>
		)
	},
)
